import microscopeConfig from '../config/microscope';
import { SERVER_URL } from '../utils/apiUtilsConstants';
import { getLogToken } from './logToken';

const POST_ERROR_SECONDS = 2;
const CHECKOUT_INTERVAL_MS = 5_000;
const MAX_CHECKOUT_CYCLES = 2;

let eventsMatrix = [[]];
let postErrorEvents = null;
let stopRecording = null;
let startPromise = null;

export function startReplayCapture() {
  if (stopRecording || startPromise) return startPromise ?? Promise.resolve();

  startPromise = import('rrweb')
    .then(({ record }) => {
      startRecording(record);
    })
    .catch((err) => {
      console.error('[Microscope Replay] Failed to load the recorder:', err);
      startPromise = null;
    });

  return startPromise;
}

function startRecording(record) {
  try {
    stopRecording = record({
      emit(event, isCheckout) {
        if (postErrorEvents) {
          postErrorEvents.push(event);
          return;
        }

        if (isCheckout || event.type === 4) {
          if (event.type === 4) {
            eventsMatrix.push([]);
            if (eventsMatrix.length > MAX_CHECKOUT_CYCLES) {
              eventsMatrix = eventsMatrix.slice(-MAX_CHECKOUT_CYCLES);
            }
          }
        }

        eventsMatrix[eventsMatrix.length - 1].push(event);
      },

      checkoutEveryNms: CHECKOUT_INTERVAL_MS,

      sampling: {
        mousemove: 300,
        mouseInteraction: true,
        scroll: 300,
        input: 'last',
      },

      blockClass: 'rr-block',
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    });

    if (stopRecording) {
      console.log('[Microscope Replay] Recording started successfully');
    } else {
      console.warn('[Microscope Replay] record() returned falsy — replay will not work');
    }
  } catch (err) {
    console.error('[Microscope Replay] Failed to start recording:', err);
    stopRecording = null;
  }
}

function trimToLastSnapshot(events) {
  let lastSnapshotIdx = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].type === 2) {
      lastSnapshotIdx = i;
      break;
    }
  }
  if (lastSnapshotIdx <= 0) return events;

  let startIdx = lastSnapshotIdx;
  for (let i = lastSnapshotIdx - 1; i >= 0; i--) {
    if (events[i].type === 4) {
      startIdx = i;
      break;
    }
  }
  return events.slice(startIdx);
}

const MAX_EVENTS = 300;
const MAX_RAW_BYTES = 2 * 1024 * 1024;

function getHeaderEndIndex(events) {
  let headerEnd = 0;
  for (let i = 0; i < Math.min(events.length, 10); i++) {
    if (events[i].type === 4 || events[i].type === 2) {
      headerEnd = i + 1;
    } else if (headerEnd > 0) {
      break;
    }
  }
  return Math.max(headerEnd, 1);
}

function capEvents(events) {
  if (events.length <= MAX_EVENTS) return events;

  const headerEnd = getHeaderEndIndex(events);
  const header = events.slice(0, headerEnd);
  const tail = events.slice(-(MAX_EVENTS - header.length));
  return [...header, ...tail];
}

function trimToFit(events) {
  let json = JSON.stringify(events);
  if (json.length <= MAX_RAW_BYTES) return { events, json };

  const headerEnd = getHeaderEndIndex(events);
  let trimmed = [...events];

  const minTail = 20;
  while (json.length > MAX_RAW_BYTES && trimmed.length > headerEnd + minTail) {
    const removable = trimmed.length - headerEnd - minTail;
    const dropCount = Math.max(Math.ceil(removable * 0.1), 1);
    trimmed = [
      ...trimmed.slice(0, headerEnd),
      ...trimmed.slice(headerEnd + dropCount),
    ];
    json = JSON.stringify(trimmed);
  }

  console.log(
    `[Microscope Replay] Trimmed from ${events.length} → ${trimmed.length} events to fit ${(json.length / 1024).toFixed(0)}KB raw`,
  );
  return { events: trimmed, json };
}

export function captureErrorReplay() {
  if (postErrorEvents) {
    console.log('[Microscope Replay] Capture skipped — another capture in progress');
    return null;
  }
  if (!getLogToken()) {
    console.log('[Microscope Replay] Capture skipped — not authenticated');
    return null;
  }

  const replayId = crypto.randomUUID();
  const beforeEvents = eventsMatrix.flat();
  console.log(`[Microscope Replay] Capturing clip ${replayId}: ${beforeEvents.length} buffered events`);

  if (beforeEvents.length === 0) {
    console.warn('[Microscope Replay] No rrweb events buffered — recording may not have started');
  }

  postErrorEvents = [];

  setTimeout(() => {
    const rawEvents = [...beforeEvents, ...(postErrorEvents || [])];
    postErrorEvents = null;

    if (rawEvents.length === 0) return;

    const allEvents = capEvents(trimToLastSnapshot(rawEvents));

    const timestamps = allEvents.map((e) => e.timestamp).filter(Boolean);
    const durationMs =
      timestamps.length >= 2
        ? timestamps[timestamps.length - 1] - timestamps[0]
        : POST_ERROR_SECONDS * 1000;

    sendReplayClip(replayId, allEvents, durationMs);
  }, POST_ERROR_SECONDS * 1000);

  return replayId;
}

async function gzipCompress(jsonString) {
  const encoder = new TextEncoder();
  const stream = new Blob([encoder.encode(jsonString)])
    .stream()
    .pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).blob();
}

const MAX_COMPRESSED_BYTES = 500 * 1024;

async function sendReplayClip(replayId, events, durationMs) {
  const { appName } = microscopeConfig;
  const token = getLogToken();
  if (!token) return;

  const { events: fittedEvents } = trimToFit(events);
  const payload = {
    replay_id: replayId,
    app_name: appName,
    events: fittedEvents,
    duration_ms: durationMs,
  };
  const json = JSON.stringify(payload);

  const rawKB = (json.length / 1024).toFixed(1);

  try {
    const compressed = await gzipCompress(json);
    const compressedKB = (compressed.size / 1024).toFixed(1);

    if (compressed.size > MAX_COMPRESSED_BYTES) {
      console.warn(
        `[Microscope Replay] Compressed size ${compressedKB}KB exceeds limit — dropping clip to avoid 413`,
      );
      return;
    }

    console.log(`[Microscope Replay] Sending clip ${replayId}: ${payload.events.length} events, ${durationMs}ms (${rawKB}KB → ${compressedKB}KB gzip)`);

    const res = await fetch(`${SERVER_URL}/api/log/replay`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Encoding': 'gzip',
        'Authorization': `Bearer ${token}`,
      },
      body: compressed,
    });
    console.log(`[Microscope Replay] Response: ${res.status}`);
  } catch (err) {
    console.log(`[Microscope Replay] Gzip failed (${err.message}), sending raw ${rawKB}KB`);

    fetch(`${SERVER_URL}/api/log/replay`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: json,
    }).catch(() => {});
  }
}
